import React from 'react';
import {
  ListItem,
  ListItemText,
  IconButton,
  Box,
  Typography,
  Button,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  FileText,
  Check,
  X,
  Trash2,
} from 'lucide-react';
import { alpha, useTheme } from '@mui/material/styles';
import DocumentDetailsCollapse from './DocumentDetailsCollapse';

const DocumentListItem = ({
  doc,
  selectedDocId,
  approvedDocs,
  localExpandedDocs,
  onSelectDocument,
  handleApproveDoc,
  handleRejectDoc,
  handleToggleExpand, // Renamed prop
  handleRequestDelete, // Corrected prop name
}) => {
  const theme = useTheme(); // Get the theme object
  return (
    <React.Fragment key={doc.id}>
      <ListItem
        disablePadding
        alignItems="flex-start"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '&:last-child': { borderBottom: 0 },
          backgroundColor: selectedDocId === doc.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
          position: 'relative', // Needed for absolute positioning of trash icon
        }}
      >
        <ListItemText
          sx={{ pl: 2, my: 0, py: 1.5, pr: theme.spacing(5) }} // Added pl: 2, kept existing vertical and right padding
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FileText size={20} style={{ marginRight: theme.spacing(1) }} /> {/* Corrected: marginRight */}
              <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'medium' }}>
                {doc.name}
              </Typography>
            </Box>
          }
          secondaryTypographyProps={{ component: 'div' }}
          secondary={
            <Box sx={{ pt: 0.5, pl: '28px' }}> {/* Indent secondary content to align with title text */}
              {/* Flex container for Type and Date */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1, mb: 1 }}>
                <Box sx={{ mr: 3 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, fontWeight: 'bold' }}>
                    Document Type
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                    {doc.type || 'Unknown'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, fontWeight: 'bold' }}>
                    Upload Date
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                    {doc.uploadedAt?.toLocaleDateString() || 'N/A'}
                  </Typography>
                </Box>
              </Box>
              {/* Summary section below Type and Date */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, fontWeight: 'bold' }}>
                  Summary
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {doc.summary || 'No summary available.'}
                </Typography>
              </Box>
              {false && (
                <>
                  {/* --- DHF Location & Approval Group --- */}
                  <Box
                    sx={theme => ({ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      mt: 2, 
                      p: 0.5,
                      maxWidth: '380px',
                      width: '380px',
                      borderRadius: 1,
                      border: `1px ${approvedDocs.has(doc.id) ? 'solid' : 'dotted'}`, 
                      borderColor: approvedDocs.has(doc.id) ? theme.palette.success.light : theme.palette.divider, 
                      backgroundColor: approvedDocs.has(doc.id) ? alpha(theme.palette.success.light, 0.15) : 'transparent',
                      transition: 'background-color 0.3s, border-color 0.3s', // Smooth transition
                    })}
                  >
                    <Box> {/* Container for text */} 
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                        DHF Location
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        Design Planning {'>'} Design Inputs {/* Placeholder - Replace with actual data */} 
                      </Typography>
                    </Box>
                    {/* Approve/Reject Buttons - Moved Here */} 
                    {approvedDocs.has(doc.id) ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error" // Reject button always error color
                          startIcon={<X size={14} />} // Smaller icon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRejectDoc(doc.id);
                          }}
                          sx={{ minWidth: 'auto', fontSize: '0.75rem', py: 0.2, px: 1, ml: 1 }} // Adjusted styling
                        >
                          Reject
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<Check size={14} />} // Smaller icon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApproveDoc(doc.id);
                          }}
                          sx={{ minWidth: 'auto', fontSize: '0.75rem', py: 0.2, px: 1, ml: 1 }} // Adjusted styling
                        >
                          Approve
                        </Button>
                      )}
                  </Box>
                </>
              )}
            </Box>
          }
        />
        {/* Expand/Collapse Button moved to bottom right */} 
        {/* <IconButton
          edge="end"
          aria-label="expand"
          onClick={(e) => {
            e.stopPropagation(); // Prevent ListItem onClick
            handleToggleExpand(doc.id); // Use the renamed prop
          }}
          sx={{ 
            position: 'absolute',
            bottom: theme.spacing(1),
            right: theme.spacing(1),
            transition: 'transform 0.2s ease-in-out',
            transform: localExpandedDocs.has(doc.id) ? 'rotate(180deg)' : 'rotate(0deg)',
            zIndex: 1,
          }}
        >
          {localExpandedDocs.has(doc.id) ? <ChevronUp /> : <ChevronDown />}
        </IconButton> */}
        <Tooltip title="Delete Document">
          <IconButton 
            aria-label="delete"
            onClick={(e) => { e.stopPropagation(); handleRequestDelete(doc); }}
            sx={{
              position: 'absolute',
              top: theme.spacing(1),
              right: theme.spacing(1),
              zIndex: 1, // Ensure it's above other content if needed
              color: 'grey.600',
              '&:hover': {
                color: 'error.main',
                backgroundColor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <Trash2 size={18} />
          </IconButton>
        </Tooltip>
      </ListItem>
      <Collapse in={localExpandedDocs.has(doc.id)} timeout="auto" unmountOnExit>
        <DocumentDetailsCollapse doc={doc} />
      </Collapse>
    </React.Fragment>
  );
};

export default DocumentListItem;
